<script setup>
import { reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['create', 'edit'].includes(value),
  },
  server: {
    type: Object,
    default: null,
  },
  showModalWrapper: {
    type: Boolean,
    default: true,
  },
  showActionButtons: {
    type: Boolean,
    default: true,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'close', 'cancel']);

const { t } = useI18n();

// Form state
const formData = reactive({
  label: '',
  url: '',
  api_key: '',
  transport_protocol: 'streamable_http',
  additional_attributes: {},
});

// Validation rules
const rules = {
  label: {
    required: helpers.withMessage(
      () => t('INTEGRATION_SETTINGS.MCP_SERVERS.VALIDATION.LABEL_REQUIRED'),
      required
    ),
  },
  url: {
    required: helpers.withMessage(
      () => t('INTEGRATION_SETTINGS.MCP_SERVERS.VALIDATION.URL_REQUIRED'),
      required
    ),
    url: helpers.withMessage(
      () => t('INTEGRATION_SETTINGS.MCP_SERVERS.VALIDATION.URL_INVALID'),
      value => {
        if (!value) return true;
        try {
          const urlObj = new URL(value);
          return urlObj.protocol === 'https:';
        } catch {
          return false;
        }
      }
    ),
    httpsRequired: helpers.withMessage(
      () => t('INTEGRATION_SETTINGS.MCP_SERVERS.VALIDATION.URL_HTTPS_REQUIRED'),
      value => {
        if (!value) return true;
        try {
          const urlObj = new URL(value);
          return urlObj.protocol === 'https:';
        } catch {
          return false;
        }
      }
    ),
    noLocal: helpers.withMessage(
      () => t('INTEGRATION_SETTINGS.MCP_SERVERS.VALIDATION.URL_NO_LOCAL'),
      value => {
        if (!value) return true;
        try {
          const urlObj = new URL(value);
          const host = urlObj.hostname;
          return !(
            host === 'localhost' ||
            host === '127.0.0.1' ||
            host.match(/^192\.168\./) ||
            host.match(/^10\./)
          );
        } catch {
          return false;
        }
      }
    ),
  },
  api_key: {
    // Removed minLength validation - API key length is not enforced
  },
};

const v$ = useVuelidate(rules, formData);

// Computed properties
const isEditMode = computed(() => props.mode === 'edit');
const dialogTitle = computed(() =>
  isEditMode.value
    ? t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.TITLE_EDIT')
    : t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.TITLE_CREATE')
);

const submitButtonText = computed(() =>
  isEditMode.value
    ? t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.ACTIONS.UPDATE')
    : t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.ACTIONS.CREATE')
);

const optionalText = computed(
  () => `(${t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.OPTIONAL')})`
);
const requiredMark = computed(() => '*');

// Transport protocol options
const transportProtocolOptions = computed(() => [
  {
    value: 'streamable_http',
    label: t(
      'INTEGRATION_SETTINGS.MCP_SERVERS.FORM.TRANSPORT_PROTOCOL.STREAMABLE_HTTP'
    ),
  },
  {
    value: 'sse',
    label: `${t(
      'INTEGRATION_SETTINGS.MCP_SERVERS.FORM.TRANSPORT_PROTOCOL.SSE'
    )} ${t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.DISABLED')}`,
  },
]);

const selectedTransportProtocolLabel = computed(() => {
  const option = transportProtocolOptions.value.find(
    opt => opt.value === formData.transport_protocol
  );
  return option ? option.label : transportProtocolOptions.value[0].label;
});

// Methods
const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  const submitData = {
    label: formData.label,
    url: formData.url,
    api_key: formData.api_key,
    transport_protocol: formData.transport_protocol,
    additional_attributes: formData.additional_attributes,
  };

  emit('submit', submitData);
};

const handleClose = () => {
  emit('close');
};

const handleCancel = () => {
  emit('cancel');
};

const handleKeydown = event => {
  if (event.key === 'Escape') {
    handleClose();
  }
};

// Initialize form data
const initializeForm = () => {
  if (isEditMode.value && props.server) {
    formData.label = props.server.label || '';
    formData.url = props.server.url || '';
    formData.api_key = ''; // Never pre-fill API key for security
    formData.transport_protocol =
      props.server.transport_protocol || 'streamable_http';
    formData.additional_attributes = props.server.additional_attributes || {};
  } else {
    formData.label = '';
    formData.url = '';
    formData.api_key = '';
    formData.transport_protocol = 'streamable_http';
    formData.additional_attributes = {};
  }
};

// Watchers
watch(() => props.server, initializeForm, { immediate: true });

// Lifecycle
onMounted(() => {
  initializeForm();
  document.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <!-- Modal Backdrop (only when showModalWrapper is true) -->
  <div
    v-if="showModalWrapper"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <!-- Modal Container -->
    <div
      class="relative w-full max-w-md max-h-[90vh] overflow-auto bg-white dark:bg-slate-800 rounded-lg shadow-xl"
      @click.stop
    >
      <!-- Header -->
      <div class="flex items-center justify-between p-6">
        <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ dialogTitle }}
        </h2>
        <Button
          size="sm"
          variant="ghost"
          color="slate"
          icon="i-lucide-x"
          @click="handleClose"
        />
      </div>

      <!-- Form -->
      <form class="px-6 pb-6 space-y-4" @submit.prevent="handleSubmit">
        <!-- Form Fields -->
        <div class="space-y-4">
          <!-- Server Name -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
            >
              {{ $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.LABEL.LABEL') }}
              <span class="text-red-500">{{ requiredMark }}</span>
            </label>
            <Input
              v-model="formData.label"
              :placeholder="
                $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.LABEL.PLACEHOLDER')
              "
              :message-type="v$.label.$error ? 'error' : 'info'"
              :message="
                v$.label.$error
                  ? $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.LABEL.ERROR')
                  : ''
              "
              @blur="v$.label.$touch()"
            />
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {{ $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.LABEL.HELP') }}
            </p>
          </div>

          <!-- Server URL -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
            >
              {{ $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.URL.LABEL') }}
              <span class="text-red-500">{{ requiredMark }}</span>
            </label>
            <Input
              v-model="formData.url"
              type="url"
              :placeholder="
                $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.URL.PLACEHOLDER')
              "
              :message-type="v$.url.$error ? 'error' : 'info'"
              :message="
                v$.url.$error
                  ? $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.URL.ERROR')
                  : ''
              "
              @blur="v$.url.$touch()"
            />
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {{ $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.URL.HELP') }}
            </p>
          </div>

          <!-- API Key -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
            >
              {{
                $t(
                  'INTEGRATION_SETTINGS.MCP_SERVERS.FORM.CREDENTIALS.API_KEY.LABEL'
                )
              }}
              <span v-if="!isEditMode" class="text-slate-500 text-xs">
                {{ optionalText }}
              </span>
            </label>
            <Input
              v-model="formData.api_key"
              type="password"
              :placeholder="
                isEditMode
                  ? $t(
                      'INTEGRATION_SETTINGS.MCP_SERVERS.FORM.CREDENTIALS.API_KEY.PLACEHOLDER_EDIT'
                    )
                  : $t(
                      'INTEGRATION_SETTINGS.MCP_SERVERS.FORM.CREDENTIALS.API_KEY.PLACEHOLDER'
                    )
              "
              autocomplete="new-password"
            />
          </div>

          <!-- Transport Protocol -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
            >
              {{
                $t(
                  'INTEGRATION_SETTINGS.MCP_SERVERS.FORM.TRANSPORT_PROTOCOL.LABEL'
                )
              }}
              <span class="text-red-500">{{ requiredMark }}</span>
            </label>
            <SelectMenu
              v-model="formData.transport_protocol"
              :options="transportProtocolOptions"
              :label="selectedTransportProtocolLabel"
            />
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {{
                $t(
                  'INTEGRATION_SETTINGS.MCP_SERVERS.FORM.TRANSPORT_PROTOCOL.HELP'
                )
              }}
            </p>
          </div>
        </div>
      </form>

      <!-- Footer -->
      <div
        v-if="showActionButtons"
        class="flex items-center justify-end gap-3 p-6"
      >
        <Button
          variant="ghost"
          color="slate"
          :label="$t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.ACTIONS.CANCEL')"
          @click="handleClose"
        />
        <Button
          :label="submitButtonText"
          :disabled="v$.$invalid || isSubmitting"
          :loading="isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>

  <!-- Inline Form (when showModalWrapper is false) -->
  <form v-else class="space-y-4" @submit.prevent="handleSubmit">
    <!-- Server Name -->
    <div>
      <label
        class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
      >
        {{ $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.LABEL.LABEL') }}
        <span class="text-red-500">{{ requiredMark }}</span>
      </label>
      <Input
        v-model="formData.label"
        :placeholder="
          $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.LABEL.PLACEHOLDER')
        "
        :message-type="v$.label.$error ? 'error' : 'info'"
        :message="
          v$.label.$error
            ? $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.LABEL.ERROR')
            : ''
        "
        @blur="v$.label.$touch()"
      />
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
        {{ $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.LABEL.HELP') }}
      </p>
    </div>

    <!-- Server URL -->
    <div>
      <label
        class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
      >
        {{ $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.URL.LABEL') }}
        <span class="text-red-500">{{ requiredMark }}</span>
      </label>
      <Input
        v-model="formData.url"
        type="url"
        :placeholder="
          $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.URL.PLACEHOLDER')
        "
        :message-type="v$.url.$error ? 'error' : 'info'"
        :message="
          v$.url.$error
            ? $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.URL.ERROR')
            : ''
        "
        @blur="v$.url.$touch()"
      />
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
        {{ $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.URL.HELP') }}
      </p>
    </div>

    <!-- API Key -->
    <div>
      <label
        class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
      >
        {{
          $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.CREDENTIALS.API_KEY.LABEL')
        }}
        <span v-if="!isEditMode" class="text-slate-500 text-xs">
          {{ optionalText }}
        </span>
      </label>
      <Input
        v-model="formData.api_key"
        type="password"
        :placeholder="
          isEditMode
            ? $t(
                'INTEGRATION_SETTINGS.MCP_SERVERS.FORM.CREDENTIALS.API_KEY.PLACEHOLDER_EDIT'
              )
            : $t(
                'INTEGRATION_SETTINGS.MCP_SERVERS.FORM.CREDENTIALS.API_KEY.PLACEHOLDER'
              )
        "
        autocomplete="new-password"
      />
    </div>

    <!-- Transport Protocol -->
    <div>
      <label
        class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
      >
        {{
          $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.TRANSPORT_PROTOCOL.LABEL')
        }}
        <span class="text-red-500">{{ requiredMark }}</span>
      </label>
      <SelectMenu
        v-model="formData.transport_protocol"
        :options="transportProtocolOptions"
        :label="selectedTransportProtocolLabel"
      />
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
        {{
          $t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.TRANSPORT_PROTOCOL.HELP')
        }}
      </p>
    </div>

    <!-- Action buttons for inline form -->
    <div
      v-if="showActionButtons"
      class="flex items-center justify-end gap-3 pt-4"
    >
      <Button
        variant="ghost"
        color="slate"
        :label="$t('INTEGRATION_SETTINGS.MCP_SERVERS.FORM.ACTIONS.CANCEL')"
        @click="handleCancel"
      />
      <Button
        :label="submitButtonText"
        :disabled="v$.$invalid || isSubmitting"
        :loading="isSubmitting"
        @click="handleSubmit"
      />
    </div>
  </form>
</template>

<style scoped>
/* Ensure modal is on top */
.fixed {
  z-index: 999;
}
</style>
